// src/services/roleService.js
import { api } from './api';
import { createService } from './factory';

// Create the base role service using the factory
const roleServiceBase = createService('Role', '/roles', {
  customEndpoints: {
    /**
     * Assign a role to a user
     * @param {string} userId - User ID
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Response with message and updated user
     */
    assignRoleToUser: async (userId, roleId) => {
      try {
        const response = await api.patch(`/users/${userId}/role`, { roleId });
        return response;
      } catch (error) {
        throw error;
      }
    }
  }
});

/**
 * Service for managing roles
 */
const roleService = {
  /**
   * Get paginated list of roles
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Items per page
   * @returns {Promise<Object>} Paginated roles response
   */
  getRoles: async (page = 1, limit = 10) => {
    try {
      return await roleServiceBase.getAll({ page, limit });
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Get a role by ID
   * @param {string} roleId - Role ID
   * @returns {Promise<Object>} Role object
   */
  getRole: async (roleId) => {
    try {
      return await roleServiceBase.getById(roleId);
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Create a new role
   * @param {Object} roleData - Role data
   * @param {string} roleData.name - Role name
   * @param {string} roleData.key - Role key (unique identifier)
   * @param {string} roleData.description - Role description
   * @param {Array} roleData.permissions - Array of permission objects
   * @returns {Promise<Object>} Created role
   */
  createRole: async (roleData) => {
    try {
      return await roleServiceBase.create(roleData);
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Update a role
   * @param {string} roleId - Role ID
   * @param {Object} roleData - Role data to update
   * @param {string} [roleData.name] - Role name
   * @param {string} [roleData.description] - Role description
   * @param {Array} [roleData.permissions] - Array of permission objects
   * @returns {Promise<Object>} Updated role
   */
  updateRole: async (roleId, roleData) => {
    try {
      return await roleServiceBase.patch(roleId, roleData);
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Delete a role
   * @param {string} roleId - Role ID
   * @returns {Promise<void>}
   */
  deleteRole: async (roleId) => {
    try {
      return await roleServiceBase.delete(roleId);
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Assign a role to a user
   * @param {string} userId - User ID
   * @param {string} roleId - Role ID
   * @returns {Promise<Object>} Response with message and updated user
   */
  assignRoleToUser: async (userId, roleId) => {
    try {
      return await roleServiceBase.assignRoleToUser(userId, roleId);
    } catch (error) {
      throw error;
    }
  }
};

export default roleService;